import { useState, useCallback } from 'react';
import { Product, SearchFiltersType } from '../pages/CatalogPage';
import { parseProduct } from '../utils/productParser';

type UseProductSearchReturn = {
  products: Product[];
  loading: boolean;
  error: string | null;
  searchProducts: (filters: SearchFiltersType) => Promise<void>;
}

export const useProductSearch = (): UseProductSearchReturn => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchProducts = useCallback(async (filters: SearchFiltersType) => {
    setLoading(true);
    setError(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value && typeof value === 'string' && value.trim()) {
          params.append(key, value.trim());
        }
      });

      const url = `/api/products${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const processedProducts = (data.data || []).map(parseProduct);
        setProducts(processedProducts);
      } else {
        throw new Error(data.error || 'Failed to fetch products');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    products,
    loading,
    error,
    searchProducts,
  };
};
